import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { normalizeCalendarDate, type v2 } from "@medbillai/schemas";
import { billUrl } from "@medbillai/utils";

import { isResolvedClosure } from "../../home/sections/resolveClosedWin";
import {
  calendarDay,
  invoiceBalance,
  parseCollectionsNotice,
  parseStatement,
} from "../utils/healthDataBill";
import type { NormalizedEob } from "../utils/types";

dayjs.extend(utc);

export type RecordKind =
  | "claim"
  | "invoice"
  | "statement"
  | "collectionsNotice";

/** The badge a record's linked case earns, highest first. */
export type RecordStatus =
  | "needsYou"
  | "newMessage"
  | "inProgress"
  | "resolved"
  | "closed";

export type StatusFilter = "all" | "needsYou" | "inProgress" | "done";

export type RecordFilters = {
  readonly status: StatusFilter;
  readonly person?: string;
};

export type RecordItem = {
  readonly key: string;
  readonly kind: RecordKind;
  readonly provider?: string;
  readonly serviceDate?: Date;
  readonly amount?: number;
  /** A paid bill: `amount` is what was paid, if the bill says. */
  readonly paid?: boolean;
  readonly decision?: string;
  readonly status?: RecordStatus;
  readonly patient?: string;
  readonly patientName?: string;
  readonly detailLine?: string;
  readonly badge?: Chip;
};

export const recordUrl = (record: RecordItem): string =>
  billUrl(
    record.key,
    record.provider ?? "",
    record.decision ?? "",
    record.kind,
  );

export type Chip = {
  readonly label: string;
  readonly tone: "negative" | "warning" | "neutral" | "positive" | "info";
};

type LinkedCase = {
  readonly closed: boolean;
  readonly hasActions: boolean;
  readonly hasUnseenStaffMessage: boolean;
  readonly caseManagementClosureReason?: string | null;
  readonly userClosureReason?: string | null;
  readonly recordBillAmount?: string | null;
  readonly negotiatedBillAmount?: string | null;
  readonly billPaidAt?: Date | null;
};

export const KIND_LABEL: Record<RecordKind, string> = {
  claim: "Claim",
  invoice: "Bill",
  statement: "Statement",
  collectionsNotice: "Collections",
};

export const SHORT_LABEL: Record<RecordKind, string> = {
  claim: "You may owe",
  invoice: "Balance",
  statement: "Total",
  collectionsNotice: "Due",
};

export const STATUS_CHIP = {
  needsYou: { label: "Needs you", tone: "warning" },
  newMessage: { label: "New message", tone: "info" },
  inProgress: { label: "In progress", tone: "neutral" },
  resolved: { label: "Resolved", tone: "positive" },
  closed: { label: "Closed", tone: "neutral" },
} as const satisfies Record<RecordStatus, { label: string; tone: string }>;

export const NOTHING_FLAGGED: Chip = {
  label: "Nothing flagged",
  tone: "neutral",
};

const PAID_CHIP: Chip = { label: "Paid", tone: "positive" };

/**
 * What's left of the record's amount after the case's saving, or what bill pay
 * charged for it, when the case's confirmed bill amount matches the record and
 * bill pay charged it or the case closed resolved.
 */
export const settledAmount = (
  linkedCase: Pick<
    LinkedCase,
    | "recordBillAmount"
    | "negotiatedBillAmount"
    | "billPaidAt"
    | "caseManagementClosureReason"
    | "userClosureReason"
  > | null,
  amount: number | undefined,
): number | undefined => {
  const billAmount = Number.parseFloat(linkedCase?.recordBillAmount ?? "");
  const negotiated = Number.parseFloat(linkedCase?.negotiatedBillAmount ?? "");
  return amount !== undefined &&
    linkedCase !== null &&
    (Boolean(linkedCase.billPaidAt) || isResolvedClosure(linkedCase)) &&
    Math.abs(billAmount - amount) <= 1 &&
    (negotiated < amount ||
      (Boolean(linkedCase?.billPaidAt) && negotiated <= billAmount))
    ? negotiated
    : undefined;
};

const withCharge = (
  item: RecordItem,
  linkedCase: LinkedCase | null | undefined,
  owed = item.amount,
): RecordItem => {
  const paid = linkedCase?.billPaidAt
    ? settledAmount(linkedCase, owed)
    : undefined;
  if (paid === undefined) {
    return item;
  }
  return item.status === "needsYou" || item.status === "newMessage"
    ? { ...item, amount: paid }
    : { ...item, amount: paid, badge: PAID_CHIP };
};

export const caseStatus = (linkedCase: LinkedCase): RecordStatus => {
  if (linkedCase.hasActions) {
    return "needsYou";
  }
  if (linkedCase.hasUnseenStaffMessage) {
    return "newMessage";
  }
  if (!linkedCase.closed) {
    return "inProgress";
  }
  return isResolvedClosure(linkedCase) ? "resolved" : "closed";
};

// Extraction often returns names in all caps, so those are recased; mixed case is kept as written.
const displayCase = (name: string): string =>
  name === name.toUpperCase()
    ? name
        .toLowerCase()
        .replace(
          /(^|[^\p{L}\p{N}'’])(\p{L})(?:(['’])(\p{L}))?/gu,
          (_, start: string, first: string, apostrophe = "", next = "") =>
            `${start}${first.toUpperCase()}${apostrophe}${next.toUpperCase()}`,
        )
    : name;

/** The first name given, skipping blanks and the feeds' "Unknown", with all-caps names recased. */
export const knownName = (
  ...names: (string | null | undefined)[]
): string | undefined => {
  const name = names
    .map(name => name?.trim())
    .find(name => name && name.toLowerCase() !== "unknown");
  return name && displayCase(name);
};

export const patientFirstName = (
  patientName: string | null | undefined,
): string | undefined => knownName(patientName)?.split(/\s+/)[0];

export const patientFullName = (
  patientName: string | null | undefined,
): string | undefined => knownName(patientName)?.replace(/\s+/g, " ");

const personKey = (name: string): string => name.toLowerCase();

export type Person = { readonly key: string; readonly label: string };

/** Empty unless two people are named; full names only where first names clash. */
export const peopleIn = (records: readonly RecordItem[]): Person[] => {
  const names = new Map<string, string>();
  for (const { patientName } of records) {
    if (patientName) {
      names.set(personKey(patientName), patientName);
    }
  }
  if (names.size < 2) {
    return [];
  }
  const firstOf = (name: string) => name.split(" ")[0] ?? name;
  const firstNames = [...names.values()].map(firstOf);
  return [...names].map(([key, name]) => ({
    key,
    label:
      firstNames.filter(first => first === firstOf(name)).length > 1
        ? name
        : firstOf(name),
  }));
};

export type RecordAttention =
  | "denied"
  | "partlyDenied"
  | "inCollections"
  | "overdue"
  | "respondBy"
  | "deadlinePassed"
  | "pending";

export type ClaimOutcome = {
  readonly denied: boolean;
  readonly partlyDenied: boolean;
  readonly pending: boolean;
};

// Uploaded PARTIAL claims also set denial.isDenied.
export const claimOutcome = (eob: v2.EOB): ClaimOutcome => {
  const { decision, denial } = eob.adjudication;
  const partlyDenied = decision === "PARTIAL";
  return {
    denied:
      !partlyDenied && (decision === "DENIED" || denial?.isDenied === true),
    partlyDenied,
    pending: decision === "PENDING",
  };
};

/** The user's share less what they've already paid. */
export const claimStillOwed = (eob: v2.EOB): number | undefined => {
  const { patientResponsibility, paidByPatient } = eob.adjudication.totals;
  return patientResponsibility === undefined || !paidByPatient
    ? patientResponsibility
    : Math.max(patientResponsibility - paidByPatient, 0);
};

export const isPharmacy = (eob: v2.EOB): boolean =>
  eob.claim.claimType === "PHARMACY";

export const recordAttention = (
  facts: Partial<ClaimOutcome> & {
    readonly kind: RecordKind;
    readonly delinquent?: boolean;
    readonly inCollections?: boolean;
    /** A notice's response deadline, YYYY-MM-DD. */
    readonly deadline?: string;
  },
  today: Date | string,
): RecordAttention | undefined => {
  if (facts.kind === "claim" && facts.denied) {
    return "denied";
  }
  if (facts.kind === "claim" && facts.partlyDenied) {
    return "partlyDenied";
  }
  if (facts.inCollections) {
    return "inCollections";
  }
  if (facts.delinquent) {
    return "overdue";
  }
  if (facts.deadline) {
    return dayjs.utc(facts.deadline).isBefore(dayjs.utc(today), "day")
      ? "deadlinePassed"
      : "respondBy";
  }
  return facts.kind === "claim" && facts.pending ? "pending" : undefined;
};

/** What the no-case card names; a pending claim keeps the quiet card. */
export type CardAttention = Exclude<RecordAttention, "pending">;

export const isCardAttention = (
  attention: RecordAttention | undefined,
): attention is CardAttention =>
  attention !== undefined && attention !== "pending";

export const attentionChip = (
  attention: RecordAttention,
  deadline?: string,
): Chip => {
  switch (attention) {
    case "denied":
      return { label: "Denied", tone: "negative" };
    case "partlyDenied":
      return { label: "Partly denied", tone: "warning" };
    case "pending":
      return { label: "Claim pending", tone: "neutral" };
    case "inCollections":
      return { label: "In collections", tone: "negative" };
    case "overdue":
      return { label: "Overdue", tone: "negative" };
    case "deadlinePassed":
      return { label: "Deadline passed", tone: "negative" };
    case "respondBy":
      return {
        label: `Respond by ${dayjs.utc(deadline).format("MMM D")}`,
        tone: "warning",
      };
  }
};

export const recordStatus = (
  kind: RecordKind,
  linkedCase: LinkedCase | null | undefined,
): RecordStatus | undefined => {
  if (!linkedCase) {
    return kind === "collectionsNotice" ? "needsYou" : undefined;
  }
  return caseStatus(linkedCase);
};

type DocumentRecord = {
  readonly key: string;
  readonly data: unknown;
  readonly linkedCase?: LinkedCase | null;
};

export const statementItem = (record: DocumentRecord): RecordItem | null => {
  const statement = parseStatement(record.data, record.key);
  return statement
    ? withCharge(
        {
          key: record.key,
          kind: "statement",
          provider: knownName(statement.providerName),
          serviceDate: calendarDay(statement.statementDate),
          amount: statement.totalBalance ?? undefined,
          status: recordStatus("statement", record.linkedCase),
          detailLine: "Statement",
          badge: record.linkedCase
            ? STATUS_CHIP[caseStatus(record.linkedCase)]
            : undefined,
        },
        record.linkedCase,
      )
    : null;
};

export const collectionsNoticeItem = (
  record: DocumentRecord,
  today: Date | string,
): RecordItem | null => {
  const notice = parseCollectionsNotice(record.data, record.key);
  const deadline = normalizeCalendarDate(notice?.responseDeadline);
  const attention = record.linkedCase
    ? undefined
    : recordAttention({ kind: "collectionsNotice", deadline }, today);
  return notice
    ? withCharge(
        {
          key: record.key,
          kind: "collectionsNotice",
          provider: knownName(notice.collectorName, notice.originalCreditor),
          serviceDate: calendarDay(notice.noticeDate),
          amount: notice.amountDue ?? undefined,
          status: recordStatus("collectionsNotice", record.linkedCase),
          detailLine: "Collections notice",
          badge: record.linkedCase
            ? STATUS_CHIP[caseStatus(record.linkedCase)]
            : attention
              ? attentionChip(attention, deadline)
              : STATUS_CHIP.needsYou,
        },
        record.linkedCase,
      )
    : null;
};

export const toRecordItem = (
  row: NormalizedEob,
  kind: RecordKind,
  linkedCase: LinkedCase | null | undefined,
  owed?: number,
): RecordItem => {
  const status = recordStatus(kind, linkedCase);
  return withCharge(
    {
      key: row.id,
      kind,
      provider: knownName(row.location, row.title),
      serviceDate: row.serviceDate ?? undefined,
      amount: row.totalCost?.amount.toNumber(),
      decision: row.decision ?? undefined,
      status,
      badge: linkedCase && status ? STATUS_CHIP[status] : undefined,
    },
    linkedCase,
    owed,
  );
};

/** A balance of 0 alone isn't paid: the bill may have been covered. */
export const isPaidInvoice = (invoice: v2.Invoice): boolean =>
  invoice.meta.isPaid === true ||
  invoice.meta.stage === "PAID" ||
  (invoiceBalance(invoice) === 0 && (invoice.totals.paidSoFar ?? 0) > 0);

export const paidAmount = (invoice: v2.Invoice): number | undefined => {
  const paidSoFar = invoice.totals.paidSoFar ?? 0;
  return paidSoFar > 0 ? paidSoFar : undefined;
};

export const withInvoice = (
  item: RecordItem,
  invoice: v2.Invoice,
): RecordItem => {
  const paid = isPaidInvoice(invoice);
  const attention = recordAttention(
    {
      kind: "invoice",
      delinquent: !paid && invoice.meta.stage === "DELINQUENT",
      inCollections: !paid && invoice.meta.stage === "IN_COLLECTIONS",
    },
    new Date(),
  );
  return {
    ...item,
    provider: knownName(invoice.provider.name, invoice.issuer.name),
    patient: patientFirstName(invoice.patient.name),
    patientName: patientFullName(invoice.patient.name),
    badge: item.badge ?? (attention ? attentionChip(attention) : undefined),
    ...(paid ? { paid: true, amount: paidAmount(invoice) } : {}),
  };
};

export const withClaim = (item: RecordItem, eob: v2.EOB): RecordItem => {
  const attention = recordAttention(
    { kind: "claim", ...claimOutcome(eob) },
    new Date(),
  );
  return {
    ...item,
    provider: knownName(eob.provider.name, eob.facility?.name),
    patient: patientFirstName(eob.patient.name),
    patientName: patientFullName(eob.patient.name),
    detailLine: isPharmacy(eob)
      ? eob.adjudication.items[0]?.description
      : undefined,
    badge: item.badge ?? (attention ? attentionChip(attention) : undefined),
  };
};

const matchesStatus = (record: RecordItem, status: StatusFilter): boolean => {
  switch (status) {
    case "all":
      return true;
    // New message rows sit under Needs you; there is no chip of their own.
    case "needsYou":
      return record.status === "needsYou" || record.status === "newMessage";
    case "inProgress":
      return record.status === "inProgress";
    case "done":
      return record.status === "resolved" || record.status === "closed";
  }
};

const matchesPerson = (record: RecordItem, person?: string): boolean =>
  person === undefined ||
  (record.patientName !== undefined &&
    personKey(record.patientName) === person);

const matchesSearch = (record: RecordItem, query: string): boolean =>
  [
    record.provider ?? "",
    KIND_LABEL[record.kind],
    record.status ? STATUS_CHIP[record.status].label : "",
    record.badge?.label ?? "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(query.trim().toLowerCase());

export const visibleRecords = (
  records: readonly RecordItem[],
  filters: RecordFilters,
  query: string,
): RecordItem[] =>
  records.filter(
    record =>
      matchesStatus(record, filters.status) &&
      matchesPerson(record, filters.person) &&
      matchesSearch(record, query),
  );

export const countNeedingYou = (
  records: readonly RecordItem[],
  filters: RecordFilters,
): number =>
  records.filter(
    record =>
      matchesStatus(record, "needsYou") &&
      matchesPerson(record, filters.person),
  ).length;

export const newestFirst = (records: readonly RecordItem[]): RecordItem[] =>
  [...records].sort(
    (a, b) =>
      (b.serviceDate?.getTime() ?? Number.NEGATIVE_INFINITY) -
      (a.serviceDate?.getTime() ?? Number.NEGATIVE_INFINITY),
  );

export const groupByMonth = (
  records: readonly RecordItem[],
): { month: string; records: RecordItem[] }[] => {
  const groups = new Map<string, RecordItem[]>();
  for (const record of records) {
    const month = record.serviceDate
      ? dayjs.utc(record.serviceDate).format("MMMM YYYY")
      : "No date";
    groups.set(month, [...(groups.get(month) ?? []), record]);
  }
  return [...groups].map(([month, rows]) => ({ month, records: rows }));
};

/** The record's column on web, alone or beside the list. */
export const RECORD_MAX_WIDTH = 640;
